import { useEffect, useMemo, useState } from "react";
import { useAtomValue } from "jotai";
import { Brokers, SyncSetting } from "../../../../types";
import { AccountAtom } from "../../../../store";
import {
  addOrUpdateSyncSetting,
  getSyncSettings,
  testSyncSetting,
} from "../../../../api";
import { useNotifications } from "../../../../hooks/useNotifications";
import { ETRADEAutoSyncConfig } from "./ETRADEAutoSyncConfig";

// Props interface for the hook
export interface ETradeAutoSyncControllerProps {
  settings?: SyncSetting;
  close?: () => void;
  readOnly?: boolean;
}

// Response interface for the hook
interface ETradeAutoSyncControllerResponse {
  readOnly?: boolean;
  config: ETRADEAutoSyncConfig;
  setConfig: (config: ETRADEAutoSyncConfig) => void;
  connectionStatus: string;
  validAPIKey: boolean;
  validAPISecret: boolean;
  saveConfig: (overrideConfig: ETRADEAutoSyncConfig) => void;
  setLinkCode: (code: string) => void;
  testConfig: (
    stage:
      | "AUTHORIZE_URL"
      | "LINK_CODE"
      | "TEST_CONNECTION"
      | "RENEW_ACCESS_TOKEN"
      | "TEST_DATA_ACCESS"
      | "RUN_FULL_CONNECTION_TEST"
  ) => void;
}

// Main hook to handle ETRADE Auto Sync Controller logic
export const useETradeAutoSyncController = (
  props: ETradeAutoSyncControllerProps
): ETradeAutoSyncControllerResponse => {
  const notifications = useNotifications();
  const accountState = useAtomValue(AccountAtom);

  // State management for configuration and connection status
  const [config, setConfig] = useState<ETRADEAutoSyncConfig>({});
  const [connectionStatus, setConnectionStatus] =
    useState<string>("NOT_CONNECTED");
  const [syncSettingIdTemp, setSyncSettingIdTemp] = useState<
    number | undefined
  >(props.settings?.syncSettingId);

  // Validating API Key and Secret using useMemo
  const validAPIKey = useMemo(() => !!config.apiKey, [config.apiKey]);
  const validAPISecret = useMemo(() => !!config.secretKey, [config.secretKey]);

  // Function to save or update sync settings
  const saveConfig = async (overrideConfig: ETRADEAutoSyncConfig) => {
    const accountId = accountState.selectedAccount?.id;
    if (!accountId) return;

    const payload = {
      accountId,
      broker: Brokers.ETrade,
      name: "ETrade Auto Sync Settings",
      settings: JSON.stringify(overrideConfig || config),
      syncSettingId: props.settings?.syncSettingId || syncSettingIdTemp,
    };

    try {
      const result = await addOrUpdateSyncSetting(
        payload as SyncSetting,
        accountId
      );
      if (result.success) {
        setSyncSettingIdTemp(result.data.syncSettingId);
      }
    } catch (error) {
      notifications.showErrorMessage("Auto Sync Settings", error as any);
    }
  };

  // Function to test configuration stages such as Authorize, Link Code, Test Connection, etc.
  const testConfig = async (
    stage:
      | "AUTHORIZE_URL"
      | "LINK_CODE"
      | "TEST_CONNECTION"
      | "RENEW_ACCESS_TOKEN"
      | "TEST_DATA_ACCESS"
      | "RUN_FULL_CONNECTION_TEST"
  ) => {
    setConnectionStatus("");
    const accountId = accountState.selectedAccount?.id;
    if (!accountId) return;

    const payload = {
      accountId,
      broker: Brokers.ETrade,
      name: "ETrade Auto Sync Settings",
      settings: JSON.stringify(config),
      syncSettingId: props.settings?.syncSettingId,
    };

    try {
      if (stage === "AUTHORIZE_URL") {
        const result = await testSyncSetting(
          payload as SyncSetting,
          accountId,
          stage
        );

        // Check if result has authorizeUrl before opening
        if (
          result &&
          typeof result === "object" &&
          "data" in result &&
          result.data?.authorizeUrl
        ) {
          window.open(result.data.authorizeUrl, "_blank");
        }
      } else if (stage === "TEST_CONNECTION") {
        setConnectionStatus("IN_PROGRESS");
        
        const result = await testSyncSetting(
          { ...payload },
          accountId,
          "GET_ACCESS_TOKEN"
        );

        // Check if result has data.success property for the actual connection status
        const isConnected = result &&
          typeof result === "object" &&
          "data" in result &&
          result.data &&
          typeof result.data === "object" &&
          "success" in result.data &&
          result.data.success;
        
        setConnectionStatus(isConnected ? "CONNECTED" : "FAILED");
        
        // If connection successful, reload settings from backend to get updated config
        if (isConnected) {
          try {
            // Refresh the settings from the backend to get the updated config with access tokens
            const updatedSettings = await getSyncSettings(accountId);
            const etradeSettings = updatedSettings.find(s => s.broker === Brokers.ETrade);
            if (etradeSettings?.settings) {
              const backendConfig = JSON.parse(etradeSettings.settings);
              setConfig(backendConfig);
            }
          } catch (error) {
            console.warn("Failed to reload settings after successful connection:", error);
            // Don't show error to user since the connection was successful
          }
        }
        
        // Show detailed error information for debugging
        if (!isConnected && result?.data) {
          const errorData = result.data;
          let errorMessage = "Connection test failed";
          
          if (errorData.error) {
            errorMessage = errorData.error;
          }
          
          let detailedMessage = errorMessage;
          if (errorData.details) {
            detailedMessage += `\n\nDetails: ${errorData.details}`;
          }
          if (errorData.stage) {
            detailedMessage += `\nStage: ${errorData.stage}`;
          }
          
          notifications.createNotification(
            "ETrade Connection Test Failed", 
            detailedMessage
          );
        }
      } else {
        // Handle other stages (AUTHORIZE_URL, RENEW_ACCESS_TOKEN, TEST_DATA_ACCESS, etc.)
        try {
          const result = await testSyncSetting({ ...payload }, accountId, stage);
          
          // Handle successful responses
          if (result && typeof result === "object" && "success" in result && result.success) {
            if (stage === "RENEW_ACCESS_TOKEN") {
              notifications.createNotification(
                "ETrade Token Renewal", 
                "Access token successfully renewed"
              );
            } else if (stage === "TEST_DATA_ACCESS") {
              notifications.createNotification(
                "ETrade Data Access Test", 
                "Successfully connected to ETrade and retrieved account data"
              );
            }
          } else if (result?.data) {
            // Handle detailed error responses
            const errorData = result.data;
            let errorMessage = `${stage} failed`;
            
            if (errorData.error) {
              errorMessage = errorData.error;
            }
            
            let detailedMessage = errorMessage;
            if (errorData.details) {
              detailedMessage += `\n\nDetails: ${errorData.details}`;
            }
            if (errorData.stage) {
              detailedMessage += `\nStage: ${errorData.stage}`;
            }
            
            notifications.createNotification(
              `ETrade ${stage} Failed`, 
              detailedMessage
            );
          }
        } catch (stageError) {
          notifications.showErrorMessage(`ETrade ${stage} Error`, stageError as any);
        }
      }
    } catch (error) {
      // Show detailed error information for top-level errors
      let errorMessage = "Unknown ETrade configuration error occurred";
      let detailedMessage = errorMessage;
      
      if (error instanceof Error) {
        errorMessage = error.message;
        detailedMessage = `${errorMessage}\n\nPlease check your ETrade API credentials and try again.`;
      } else if (typeof error === "string") {
        errorMessage = error;
        detailedMessage = `${errorMessage}\n\nPlease check your ETrade API credentials and try again.`;
      }
      
      notifications.createNotification("ETrade Configuration Error", detailedMessage);
    }
  };

  // Function to set link code and update configuration
  const setLinkCode = async (code: string) => {
    const updatedConfig = { ...config, code };
    setConfig(updatedConfig);
    saveConfig(updatedConfig);
  };

  // Handle invalid configuration by clearing the code and showing a notification
  const handleInvalidConfig = () => {
    const clearedConfig = { ...config, code: "" };
    setConfig(clearedConfig);
    saveConfig(clearedConfig);
    notifications.createNotification(
      "Auto Sync Settings - Failed",
      "Unable to verify settings"
    );
  };

  // Effect to load existing settings into the config state when settings prop changes
  useEffect(() => {
    if (props.settings?.settings) {
      const parsedConfig = JSON.parse(props.settings.settings);
      setConfig(parsedConfig);
      
      // Set initial connection status based on existing config
      if (parsedConfig.accessToken && parsedConfig.accessTokenSecret) {
        setConnectionStatus("CONNECTED");
      }
    }
  }, [props.settings]);

  return {
    readOnly: props.readOnly,
    config,
    setConfig,
    validAPIKey,
    validAPISecret,
    setLinkCode,
    saveConfig,
    testConfig,
    connectionStatus,
  };
};
